/**
 * Sub change orders by direction — show spread when owner CO linked; unmatched flag card.
 */
import { Pencil, Trash2 } from 'lucide-react'
import { formatMoney } from '../../../../lib/formatters'
import type { ChangeOrderTwoWay } from '../../../../api/costsMargin'

export default function SubCosSection({
  changeOrders,
  unmatched,
  vendorNames,
  onEdit,
  onDelete,
}: {
  changeOrders: ChangeOrderTwoWay[]
  unmatched: ChangeOrderTwoWay[]
  vendorNames: Map<number, string>
  onEdit?: (co: ChangeOrderTwoWay) => void
  onDelete?: (co: ChangeOrderTwoWay) => void
}) {
  const subs = changeOrders.filter(
    (co) => co.direction === 'sub' && co.status !== 'void' && co.status !== 'voided',
  )
  const owners = changeOrders.filter(
    (co) => (co.direction === 'owner' || !co.direction) && co.status !== 'void' && co.status !== 'voided',
  )

  if (subs.length === 0 && unmatched.length === 0) return null

  return (
    <div data-testid="cm-sub-cos-section">
      {unmatched.length > 0 && (
        <div className="cm-unmatched-card" data-testid="cm-unmatched-sub-cos" role="status">
          <b>{unmatched.length} unmatched sub change order{unmatched.length === 1 ? '' : 's'}</b>
          <span> No linked owner CO — margin still includes these costs. Link when you know the pair.</span>
        </div>
      )}

      <div className="cm-section-title" style={{ borderTop: '1px solid var(--line)' }}>
        <h3>Sub change orders</h3>
        <span>Raises your cost, not your owner contract</span>
      </div>
      <div className="cm-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Sub</th>
              <th>Owner CO</th>
              <th>Spread</th>
              <th>Amount</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {subs.map((co) => {
              const linked = co.linked_change_order_id
                ? owners.find((o) => o.id === co.linked_change_order_id)
                : null
              const ownerWithSpread = linked
                || owners.find((o) => o.id === co.linked_change_order_id)
              const spread =
                ownerWithSpread?.linked_pair_spread != null
                  ? formatMoney(ownerWithSpread.linked_pair_spread)
                  : linked
                    ? '—'
                    : 'Not linked'
              const subName =
                co.vendor_name
                || (co.vendor_id != null ? vendorNames.get(co.vendor_id) : null)
                || '—'
              return (
                <tr key={co.id} data-testid={`cm-sub-co-${co.id}`}>
                  <td><strong>{co.description}</strong></td>
                  <td>{subName}</td>
                  <td>{linked ? `CO #${linked.co_number}` : 'Not linked'}</td>
                  <td className="money">{spread}</td>
                  <td className="money">{formatMoney(co.amount)}</td>
                  <td className="hub-row-actions-cell">
                    {(onEdit || onDelete) && (
                      <span className="hub-row-actions">
                        {onEdit && (
                          <button
                            type="button"
                            className="hub-row-action-btn"
                            aria-label={`Edit ${co.description}`}
                            onClick={() => onEdit(co)}
                          >
                            <Pencil size={16} strokeWidth={2} aria-hidden />
                          </button>
                        )}
                        {onDelete && (
                          <button
                            type="button"
                            className="hub-row-action-btn hub-row-action-btn--delete"
                            aria-label={`Delete ${co.description}`}
                            onClick={() => onDelete(co)}
                          >
                            <Trash2 size={16} strokeWidth={2} aria-hidden />
                          </button>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
